import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ReviewerAuthProvider, useReviewerAuth } from './context/ReviewerAuthContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './context/ConfirmContext';
import ErrorBoundary from './components/ErrorBoundary';

import Login from './pages/Login';
import VerifyMagicLink from './pages/VerifyMagicLink';
import Dashboard from './pages/Dashboard';
import Submit from './pages/Submit';
import Deliver from './pages/Deliver';
import Deliveries from './pages/Deliveries';
import Success from './pages/Success';
import Profile from './pages/Profile';
import Guidelines from './pages/Guidelines';
import HowToSubmit from './pages/HowToSubmit';
import PrivacyPolicy from './pages/PrivacyPolicy';
import FAQ from './pages/FAQ';
import AdminPanel from './pages/AdminPanel';
import ReviewerLogin from './pages/ReviewerLogin';
import ReviewerDashboard from './pages/ReviewerDashboard';
import ReviewerReview from './pages/ReviewerReview';
import { ReviewerNdaBoundary } from './pages/ReviewerNdaGate';
import ReviewerAgreementView from './pages/ReviewerAgreementView';
import NotFound from './pages/NotFound';

const Spinner = () => (
  <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)' }}>
    <div style={{ width:32, height:32, border:'3px solid var(--border)', borderTopColor:'var(--pink)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
  </div>
);

const PrivateRoute = ({ children, adminOnly=false }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/" />;
  if (adminOnly && user.role !== 'admin' && user.role !== 'super_admin') return <Navigate to="/dashboard" />;
  return children;
};

const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) return <Navigate to={(user.role === 'admin' || user.role === 'super_admin') ? '/admin' : '/dashboard'} />;
  return children;
};

const ReviewerRoute = ({ children }) => {
  const { reviewer, loading } = useReviewerAuth();
  if (loading) return <Spinner />;
  if (!reviewer) return <Navigate to="/reviewer" />;
  return <ReviewerNdaBoundary reviewer={reviewer}>{children}</ReviewerNdaBoundary>;
};

function App() {
  return (
    <ErrorBoundary>
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <ToastProvider>
        <ConfirmProvider>
        <ReviewerAuthProvider>
          <AuthProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
                <Route path="/verify" element={<VerifyMagicLink />} />
                <Route path="/guidelines" element={<Guidelines />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path="/submit" element={<PrivateRoute><Submit /></PrivateRoute>} />
                <Route path="/how-to-submit" element={<PrivateRoute><HowToSubmit /></PrivateRoute>} />
                <Route path="/deliver" element={<PrivateRoute><Deliveries /></PrivateRoute>} />
                <Route path="/deliver/:submissionId" element={<PrivateRoute><Deliver /></PrivateRoute>} />
                <Route path="/success" element={<PrivateRoute><Success /></PrivateRoute>} />
                <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                <Route path="/admin" element={<PrivateRoute adminOnly><AdminPanel /></PrivateRoute>} />
                <Route path="/reviewer" element={<ReviewerLogin />} />
                <Route path="/reviewer/dashboard" element={<ReviewerRoute><ReviewerDashboard /></ReviewerRoute>} />
                <Route path="/reviewer/review/:assignmentId" element={<ReviewerRoute><ReviewerReview /></ReviewerRoute>} />
                <Route path="/reviewer/agreement" element={<ReviewerRoute><ReviewerAgreementView /></ReviewerRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </ReviewerAuthProvider>
        </ConfirmProvider>
        </ToastProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
